/**
 * @file PhotoTypeSelect Molecule Component
 * @description 사진 타입 선택 드롭다운 컴포넌트
 * @see docs/plans/loading/05-implementation.md - Section 35
 */

import React, { useMemo, useCallback, memo } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { useTranslation } from 'react-i18next';
import Select from '@/src/components/atom/Select';
import type { SelectProps } from '@/src/components/atom/Select';
import colorSet from '@/src/styles/color';
import {
  EDITABLE_PHOTO_TYPE_CODES,
  DEFAULT_PHOTO_TYPE_SHORT_LABELS,
  type PhotoTypeCode,
} from '@/src/store/apis/photoType/interface';

export interface PhotoTypeSelectProps
  extends Omit<SelectProps, 'options' | 'value' | 'onChange'> {
  value: PhotoTypeCode;
  onChange: (value: PhotoTypeCode) => void;
  allowCustom?: boolean;
  hasError?: boolean;
}

const StyledSelect = styled(Select)<{ $hasError?: boolean }>`
  width: 180px;

  .ant-select-selector {
    border: 1px solid #e7edf0 !important;
    border-radius: 8px !important;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.05) !important;
    padding: 8px 16px !important;
    height: 40px !important;
  }

  &:hover .ant-select-selector {
    border-color: ${colorSet.gray7} !important;
  }

  &.ant-select-focused .ant-select-selector {
    border-color: ${colorSet.blue4} !important;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.05), 0 0 0 2px rgba(22, 109, 215, 0.1) !important;
  }

  .ant-select-selection-item {
    line-height: 30px !important;
  }

  ${({ $hasError }) =>
    $hasError &&
    `
    .ant-select-selector {
      border-color: ${colorSet.red2} !important;
    }
  `}
`;

const DropdownGlobalStyle = createGlobalStyle`
  .photo-type-select-dropdown {
    z-index: 10000 !important;
  }
`;

/**
 * PhotoTypeSelect - 사진 타입 선택 컴포넌트
 *
 * @description
 * 기본 7가지 사진 타입과 커스텀 타입을 선택할 수 있는 드롭다운입니다.
 * - 기본 타입: 중복 선택 불가 (이미 사용 중인 타입은 비활성화)
 * - 커스텀 타입: 자유롭게 추가 가능
 *
 * @example
 * ```tsx
 * <PhotoTypeSelect
 *   value={slot.typeCode}
 *   onChange={(value) => handleTypeChange(index, value)}
 *   allowCustom
 * />
 * ```
 */
const PhotoTypeSelectComponent: React.FC<PhotoTypeSelectProps> = ({
  value,
  onChange,
  allowCustom = true,
  hasError = false,
  ...rest
}) => {
  const { t } = useTranslation(['photoType', 'common']);

  const options = useMemo(() => {
    const defaultOptions = EDITABLE_PHOTO_TYPE_CODES.map((code) => ({
      value: code,
      label: DEFAULT_PHOTO_TYPE_SHORT_LABELS[code],
      disabled: false,
    }));

    if (allowCustom) {
      return [
        ...defaultOptions,
        {
          value: value.startsWith('CUSTOM') ? value : 'CUSTOM',
          label: 'Custom',
          disabled: false,
        },
      ];
    }

    return defaultOptions;
  }, [allowCustom, value]);

  const handleChange = useCallback(
    (selectedValue: unknown) => {
      onChange(selectedValue as PhotoTypeCode);
    },
    [onChange]
  );

  return (
    <>
      <DropdownGlobalStyle />
      <StyledSelect
        value={value}
        onChange={handleChange}
        options={options}
        $hasError={hasError}
        placeholder={t('photoType:selectType')}
        popupClassName="photo-type-select-dropdown"
        getPopupContainer={() => document.body}
        {...rest}
      />
    </>
  );
};

PhotoTypeSelectComponent.displayName = 'PhotoTypeSelect';

export const PhotoTypeSelect = memo(PhotoTypeSelectComponent);
export default PhotoTypeSelect;
